import { useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";

import { useBulkActionContext } from ".";

function getStatusLabel(status: string | null | undefined): string {
  if (status === "VERIFIED") {
    return "Verified";
  }
  if (status === "NOT_STARTED") {
    return "Not Started";
  }
  if (status) {
    return status
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  }
  return "Unknown";
}

const BULK_UPDATE_NEEDS_REVISION = gql(`
  mutation BulkUpdateTierIIReportNeedsRevision(
    $tierIIReportIds: [ID!]!
    $needsRevision: Boolean!
    $needsRevisionReason: String
  ) {
    bulkUpdateTierIIReportNeedsRevision(
      tierIIReportIds: $tierIIReportIds
      needsRevision: $needsRevision
      needsRevisionReason: $needsRevisionReason
    ) {
      updatedCount
    }
  }
`);

export function SetNeedsRevisionMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("setNeedsRevision")}>
      <ListItemIcon>
        <FlagOutlined />
      </ListItemIcon>
      <ListItemText>Set Needs Revision</ListItemText>
    </MenuItem>
  );
}

export function SetNeedsRevisionDialog() {
  const { dialogState, onClose, selectedRows } = useBulkActionContext();
  const theme = useTheme();
  const alerts = useAlerts();
  const [reason, setReason] = useState("");

  const [bulkUpdate, { loading: isUpdating }] = useMutation(BULK_UPDATE_NEEDS_REVISION, {
    onCompleted(result) {
      const count = result.bulkUpdateTierIIReportNeedsRevision.updatedCount;
      alerts.success(
        `Successfully marked ${count} ${pluralize("report", count)} as needing revision.`
      );
      onClose();
    },
    onError(error) {
      alerts.error("Error updating reports", error);
    },
    refetchQueries: ["ChemicalFacilities"],
  });

  // Split rows into eligible (verified reports), not verified, and no report
  const eligibleRows = useMemo(
    () => selectedRows.filter((row) => row.tierIIReportId && row.organizationStatus === "VERIFIED"),
    [selectedRows]
  );

  const rowsNotVerified = useMemo(
    () => selectedRows.filter((row) => row.tierIIReportId && row.organizationStatus !== "VERIFIED"),
    [selectedRows]
  );

  const rowsWithoutReports = useMemo(
    () => selectedRows.filter((row) => !row.tierIIReportId),
    [selectedRows]
  );

  const handleSubmit = useCallback(async () => {
    const tierIIReportIds = eligibleRows
      .map((row) => row.tierIIReportId)
      .filter((id): id is string => !!id);

    await bulkUpdate({
      variables: {
        tierIIReportIds,
        needsRevision: true,
        needsRevisionReason: reason.trim() || null,
      },
    });
  }, [bulkUpdate, eligibleRows, reason]);

  const handleClose = useCallback(() => {
    setReason("");
    onClose();
  }, [onClose]);

  if (dialogState !== "setNeedsRevision") {
    return null;
  }

  const canSubmit = eligibleRows.length > 0;
  const skippedCount = rowsNotVerified.length + rowsWithoutReports.length;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      title="Set Needs Revision"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: `Mark ${eligibleRows.length} ${pluralize("Report", eligibleRows.length)}`,
        loading: isUpdating,
        disabled: !canSubmit,
      }}
    >
      {eligibleRows.length > 0 ? (
        <Typography variant="body1" color="text.secondary">
          You are about to mark{" "}
          <strong>
            {eligibleRows.length} {pluralize("report", eligibleRows.length)}
          </strong>{" "}
          as needing revision.
        </Typography>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No verified reports available to update.
        </Typography>
      )}

      {skippedCount > 0 && (
        <Box
          sx={{
            p: 1.5,
            border: `1px solid ${theme.palette.warning.main}`,
            borderRadius: 1,
            backgroundColor: theme.palette.warning.light + "20",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <WarningAmber color="warning" fontSize="small" />
            <Typography variant="body2" fontWeight={500}>
              {skippedCount} {pluralize("facility", skippedCount)} will be skipped
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {rowsNotVerified.length > 0 && rowsWithoutReports.length > 0
              ? `${rowsNotVerified.length} ${pluralize("report", rowsNotVerified.length)} not yet verified, ${rowsWithoutReports.length} ${pluralize("facility", rowsWithoutReports.length)} without a report.`
              : rowsNotVerified.length > 0
                ? "Only verified reports can be marked as needing revision."
                : "These facilities do not have a report yet."}
          </Typography>
        </Box>
      )}

      <TextField
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        label="Reason for revision (optional)"
        fullWidth
        multiline
        minRows={2}
        maxRows={4}
        placeholder="Reason for revision (optional)"
      />

      <DataGrid
        columns={[
          {
            field: "organization",
            headerName: "Organization",
            flex: 1,
            minWidth: 120,
          },
          {
            field: "facilityName",
            headerName: "Facility",
            flex: 1,
            minWidth: 120,
          },
          {
            field: "state",
            headerName: "State/Territory",
            width: 60,
          },
          {
            field: "organizationStatus",
            headerName: "Report Status",
            width: 100,
            renderCell: (params) => {
              const status = params.row.organizationStatus;
              if (!params.row.tierIIReportId) {
                return (
                  <Typography variant="caption" color="text.disabled">
                    No Report
                  </Typography>
                );
              }
              return (
                <Typography
                  variant="caption"
                  color={status === "VERIFIED" ? "success.main" : "text.secondary"}
                >
                  {getStatusLabel(status)}
                </Typography>
              );
            },
          },
          {
            field: "eligible",
            headerName: "Eligible",
            width: 70,
            renderCell: (params) => {
              const hasReport = !!params.row.tierIIReportId;
              const isVerified = params.row.organizationStatus === "VERIFIED";
              if (hasReport && isVerified) {
                return <Check color="success" fontSize="small" />;
              }
              return null;
            },
          },
        ]}
        rows={selectedRows}
        getRowId={(row) => row.id}
        rowHeight={36}
        disableRowSelectionOnClick
        hideFooter={selectedRows.length <= 10}
        sx={{
          maxHeight: 300,
        }}
      />
    </Dialog>
  );
}
